import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import Table from '../../../components/Table/Table';
import Header from '../../../common/Header/Header';
import Button from '../../../components/Button/Button';
import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import {
  ExtractCouponGroupsPage,
  TableRowWrapper,
  TableWrapper,
  Buttons,
  DateHelperText,
  ColumnWrapper,
} from './ExtractCouponGroup.styles';
import { InputWrapperMedium } from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Input from '../../../components/Input/Input';
import { useGetCouponGroups } from '../../../services/couponGroup/couponGroup.service';
import { CouponGroup } from '../../../services/couponGroup/couponGroup.types';
import { useExtractCouponGroup } from '../../../services/extract/extract.service';
import Checkbox from '../../../components/Checkbox/Checkbox';

const ExtractCouponGroup: FC = () => {
  const availableGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);
  const todays_date = new Date();
  const [formValues, setFormValues] = useState({
    effectiveDate: todays_date.toISOString().slice(0, 10),
  });
  const [selectedCouponGroups, setSelectedCouponGroups] = useState<CouponGroup[]>([]);
  const { data } = useGetCouponGroups();
  const availableCouponGroups = data?.data || [];

  const navigateWithToast = useNavigateWithToast();
  const { mutate, isLoading } = useExtractCouponGroup();

  const selectAvailableCouponGroups = (selectedRows: CouponGroup[]) => {
    availableGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find(
        (selectedCouponGroup) => selectedCouponGroup.couponGroupId === node?.data?.couponGroupId
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAvailableCouponGroups(event.api.getSelectedRows());
  };

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedCouponGroups(event.api.getSelectedRows());
    selectedGridRef.current?.api.selectAll();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleClick = () => {
    mutate(
      {
        couponGroups: selectedCouponGroups.map((couponGroup) => couponGroup.couponGroupId),
        effectiveDate: new Date(formValues.effectiveDate).toISOString(),
      },
      {
        onSuccess: () => {
          navigateWithToast('/extract', {
            label: 'Coupon Group Extract was submitted',
            id: 'extractCouponGroupSuccess',
          });
        },
        onError: (error) => {
          if ((error as AxiosError).message === 'apiGatewayTimeout') return;
          showToast({
            ...genericErrorShowToastOptions,
            label: 'Error extracting coupon groups.',
            id: 'extractCouponGroupError',
            toastType: ToastTypes.Error,
          });
        },
      }
    );
  };

  const CustomHeaderCheckbox = ({ api }: any) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isIndeterminate, setIsIndeterminate] = useState(false);
    const lastSelectedCountRef = useRef(0);

    useEffect(() => {
      if (!api || availableCouponGroups.length === 0) return;

      const currentSelectedCount = selectedCouponGroups.length;
      if (currentSelectedCount === lastSelectedCountRef.current) return;

      lastSelectedCountRef.current = currentSelectedCount;

      let displayedNodeCount = 0;
      let displayedSelectedCount = 0;

      api.forEachNodeAfterFilterAndSort((node: any) => {
        if (node.displayed) {
          displayedNodeCount++;
          if (node.isSelected()) {
            displayedSelectedCount++;
          }
        }
      });

      if (selectedCouponGroups.length === availableCouponGroups.length) {
        setIsChecked(true);
        setIsIndeterminate(false);
      } else if (displayedSelectedCount === 0) {
        setIsChecked(false);
        setIsIndeterminate(false);
      } else if (displayedSelectedCount === displayedNodeCount && displayedNodeCount > 0) {
        setIsChecked(true);
        setIsIndeterminate(false);
      } else {
        setIsChecked(false);
        setIsIndeterminate(true);
      }
    }, [selectedCouponGroups, api, availableCouponGroups.length]);

    const handleCheckboxClick = useCallback(() => {
      const newCheckedState = !isChecked;
      setIsChecked(newCheckedState);
      setIsIndeterminate(false);

      if (newCheckedState) {
        api.selectAllFiltered();
      } else {
        api.deselectAllFiltered();
      }

      const selectedNodes = api.getSelectedNodes();
      const selectedRows = selectedNodes.map((node: any) => node.data);

      if (newCheckedState) {
        const existingIds = new Set(selectedCouponGroups.map((item) => item.couponGroupId));
        const newSelectedGroups = [...selectedCouponGroups];

        selectedRows.forEach((row: any) => {
          if (!existingIds.has(row.couponGroupId)) {
            newSelectedGroups.push(row);
          }
        });

        setSelectedCouponGroups(newSelectedGroups);
      } else {
        const deselectedIds = new Set(selectedRows.map((row: any) => row.couponGroupId));
        setSelectedCouponGroups(selectedCouponGroups.filter((item) => !deselectedIds.has(item.couponGroupId)));
      }
    }, [api, isChecked, selectedCouponGroups]);

    return (
      <Checkbox
        label='Select all'
        id='selectAllCheckbox'
        checked={isChecked}
        indeterminate={isIndeterminate}
        onChange={handleCheckboxClick}
        isBold={true}
      />
    );
  };

  return (
    <>
      <Header breadcrumb={{ to: '/extract', label: 'Back to Extract Queue' }} title={'Extract Coupon Groups'} />
      <ExtractCouponGroupsPage>
        <ColumnWrapper>
          <InputWrapperMedium>
            <Input
              label='Set Effective Date'
              id='effectiveDate'
              type='date'
              min={todays_date.toISOString().slice(0, 10)}
              value={formValues.effectiveDate}
              onChange={handleChange}
            />
          </InputWrapperMedium>
          <DateHelperText>Default date publishes immediately</DateHelperText>
          <TableRowWrapper>
            <TableWrapper>
              <Table
                showHeader
                headerTitle='Available Coupon Groups'
                rowData={availableCouponGroups}
                columnDefs={[
                  {
                    headerName: 'Select All',
                    checkboxSelection: true,                    maxWidth: 200,
                    width: 70,
                    floatingFilter: false,
                    flex: 1,
                    headerComponent: CustomHeaderCheckbox,
                  },
                  { headerName: 'Group ID', resizable: false, field: 'couponGroupId', flex: 2 },
                ]}
                returnedRecords={availableCouponGroups.length}
                height={'calc(100vh - 266px)'}
                onSelectionChanged={onSelectionChangedAvailable}
                ref={availableGridRef}
                rowSelection='multiple'
                suppressRowClickSelection={false}
                rowMultiSelectWithClick={true}
              />
            </TableWrapper>
            <TableWrapper>
              <Table
                showHeader
                headerTitle='Selected Coupon Groups'
                columnDefs={[
                  {
                    headerName: 'Select',
                    checkboxSelection: true,
                    maxWidth: 70,
                    width: 70,
                    floatingFilter: false,
                    flex: 1,
                  },
                  { headerName: 'Group ID', resizable: false, field: 'couponGroupId', flex: 2 },
                ]}
                rowData={selectedCouponGroups}
                height={'calc(100vh - 266px)'}
                returnedRecords={selectedCouponGroups.length}
                onSelectionChanged={onSelectionChangedSelected}
                ref={selectedGridRef}
                suppressRowClickSelection={true}
                rowSelection='multiple'
              />
            </TableWrapper>
          </TableRowWrapper>
        </ColumnWrapper>
        <Buttons>
          <Button
            label={'Extract'}
            disabled={selectedCouponGroups.length === 0}
            onClick={handleClick}
            isLoading={isLoading}
            testId='extractCouponGroupActionButton'
            loadingLabel={'Loading'}
          />
        </Buttons>
      </ExtractCouponGroupsPage>
    </>
  );
};

export default ExtractCouponGroup;
